import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ColorV2 } from "config/colors";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Modal } from "components/designV2/modal/modal";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Dropdown } from "components/designV2/dropdown/dropdown";
import { CustomIcon } from "components/icon/customIcon.component";
import { DOCUMENT_FRONTEND_ROOT } from "services/baseURLs.config";
import type { StaffProfileData } from "services/api/company/staff.service";
import { useWorkerAvatar } from "web-apps/company/components/common/hooks/useWorkerAvatar";
import {
  Section,
  SectionTitle,
  Message,
  FieldLabel,
} from "../../layout/profilePanel.styled";
import {
  useAgreementsSectionLogic,
  CONTRACT_EMPLOYMENT_TYPE_OPTIONS,
  WORK_SCHEDULE_ARRANGEMENT_OPTIONS,
  PROBATION_UNIT_OPTIONS,
} from "./profilePanel.agreementsSection.logic";
import {
  AgreementsContainer,
  RowLayout,
  RowInfo,
  RowTitle,
  RowActions,
  StatusPill,
  ContractTypeTag,
  CopyToast,
  CreateModalContent,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  ParticipantList,
  ParticipantRow,
  PersonCell,
  AvatarCircle,
  AvatarImage,
  PersonText,
  PersonName,
  PersonNumber,
  ExpandIconWrapper,
  SignaturesRatio,
  TransparentBox,
  CreateModalScroll,
  FormGrid,
  StyledTextArea,
  ProbationContainer,
  ProbationInputs,
  FieldHelpText,
  CalculatedSalaryNote,
  SpecialTermsHeader,
  SpecialTermsLabel,
  SpecialTermsHelpText,
  SpecialTermsInfo,
} from "./profilePanel.agreementsSection.styled";

interface ProfilePanelAgreementsSectionProps {
  profile: StaffProfileData | null;
  companyId: number | null;
  workerId: number | null;
}

export const ProfilePanelAgreementsSection = (
  props: ProfilePanelAgreementsSectionProps,
): JSX.Element => {
  const { t } = useTranslation();
  const logic = useAgreementsSectionLogic({
    companyId: props.companyId,
    workerId: props.workerId,
    profile: props.profile,
  });

  const workerAvatarUrl = useWorkerAvatar({
    workerExternalId: props.profile?.externalId || undefined,
    isWorker: true,
  });

  const [expandedDocs, setExpandedDocs] = useState<Record<string, boolean>>({});
  const hasAgreements = logic.agreements.length > 0;

  const toggleExpand = (docId: string) => {
    setExpandedDocs((prev) => ({
      ...prev,
      [docId]: !prev[docId],
    }));
  };

  return (
    <Section>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
        }}
      >
        <SectionTitle>{t("Staff.ProfilePanelAgreementsTitle")}</SectionTitle>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SecondaryButton
            size="small"
            label={t("Staff.ProfilePanelUploadAgreement", "Ladda upp avtal")}
            iconName="document"
            onClick={() => logic.setIsUploadModalOpen(true)}
          />
          <PrimaryButton
            size="small"
            label={t("Staff.ProfilePanelCreateAgreement")}
            onClick={() => logic.setIsCreateModalOpen(true)}
          />
        </div>
      </div>

      {logic.copySuccess && (
        <CopyToast>{t("Staff.ProfilePanelSigningLinkCopied")}</CopyToast>
      )}

      <TransparentBox>
        {logic.loading ? (
          <Message>{t("Staff.ProfilePanelLoading")}</Message>
        ) : !hasAgreements ? (
          <Message>{t("Staff.ProfilePanelNoAgreements")}</Message>
        ) : (
          <AgreementsContainer>
            {logic.agreements.map(({ doc, details }) => {
              const isImported = Boolean(
                details?.isImported || (doc as any).isImported,
              );
              const isCompleted = doc.status === "Completed" || isImported;
              const canExpand = !isCompleted;
              const docId = doc.documentId || String(doc.id);
              const docName = doc.documentName || "Agreement";
              const isOpen = canExpand && !!expandedDocs[docId];

              // Calculate participant signature count
              const participantsList = details?.participants || [];
              const totalCount = participantsList.length;
              const signedCount = participantsList.filter(
                (p) => p.status === "signed" || p.status === "completed",
              ).length;

              const contractTypeRaw =
                details?.contractType ||
                details?.category ||
                (doc as any).contractType ||
                (doc as any).category;
              const displayContractType =
                contractTypeRaw === "employment"
                  ? t(
                      "Staff.ProfilePanelCategoryEmployment",
                      "Anställningsavtal",
                    )
                  : t("Staff.ProfilePanelCategoryOther", "Övrigt");

              return (
                <AccordionItem key={docId}>
                  <AccordionHeader
                    onClick={() => canExpand && toggleExpand(docId)}
                    style={{ cursor: canExpand ? "pointer" : "default" }}
                  >
                    <RowLayout>
                      <RowInfo>
                        <RowTitle>{docName}</RowTitle>
                        <ContractTypeTag style={{ marginLeft: "8px" }}>
                          {displayContractType}
                        </ContractTypeTag>
                        {totalCount > 0 && !isCompleted && !isImported && (
                          <SignaturesRatio style={{ marginLeft: "8px" }}>
                            {signedCount}/{totalCount}{" "}
                            {t("Staff.AgreementSignedCountSuffix", "signerat")}
                          </SignaturesRatio>
                        )}
                        {!isImported && (
                          <StatusPill
                            $status={doc.status}
                            style={{ marginLeft: "8px" }}
                          >
                            {t(`Staff.agreementStatus${doc.status}`, doc.status)}
                          </StatusPill>
                        )}
                        {doc.companyName && (
                          <FieldLabel style={{ marginLeft: "8px" }}>
                            {doc.companyName}
                          </FieldLabel>
                        )}
                      </RowInfo>

                      <RowActions onClick={(e) => e.stopPropagation()}>
                        {isCompleted ? (
                          <SecondaryButton
                            size="small"
                            iconName="v2/download"
                            title={t("Staff.ProfilePanelDownloadAgreement")}
                            aria-label={t("Staff.ProfilePanelDownloadAgreement")}
                            onClick={() =>
                              logic.handleDownloadSignedAgreement(
                                docId,
                                docName,
                              )
                            }
                          />
                        ) : (
                          <ExpandIconWrapper
                            $isExpanded={isOpen}
                            onClick={() => toggleExpand(docId)}
                            style={{ cursor: "pointer", padding: "4px" }}
                          >
                            <CustomIcon
                              name="chevron-down"
                              size="20px"
                              color={ColorV2.Blue1}
                            />
                          </ExpandIconWrapper>
                        )}
                      </RowActions>
                    </RowLayout>
                  </AccordionHeader>

                  {canExpand && (
                    <AccordionBody $isOpen={isOpen}>
                      <ParticipantList>
                        {participantsList.map((participant) => {
                          const isSigned =
                            participant.status === "signed" ||
                            participant.status === "completed";
                          const pName = participant.name || "Signee";
                          const workerName =
                            `${props.profile?.firstName || ""} ${props.profile?.lastName || ""}`
                              .trim()
                              .toLowerCase();
                          const isWorker =
                            participant.name?.trim().toLowerCase() ===
                            workerName;

                          const roleLabel = isWorker
                            ? t("Staff.AgreementRoleEmployee")
                            : t("Staff.AgreementRoleEmployer");

                          const avatarUrl = isWorker ? workerAvatarUrl : null;
                          const uniqueSigningUrl = `${DOCUMENT_FRONTEND_ROOT}/document/${docId}?participantId=${participant.id}`;

                          return (
                            <ParticipantRow key={participant.id}>
                              <PersonCell>
                                <AvatarCircle>
                                  {avatarUrl ? (
                                    <AvatarImage src={avatarUrl} alt={pName} />
                                  ) : (
                                    <CustomIcon
                                      name="v2/person"
                                      size="20px"
                                      color={ColorV2.Blue3}
                                    />
                                  )}
                                </AvatarCircle>
                                <PersonText>
                                  <PersonName>{pName}</PersonName>
                                  <PersonNumber>{roleLabel}</PersonNumber>
                                </PersonText>
                              </PersonCell>

                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "16px",
                                }}
                              >
                                <StatusPill
                                  $status={isSigned ? "completed" : "pending"}
                                >
                                  {isSigned
                                    ? t("Staff.agreementStatusCompleted")
                                    : t("Staff.agreementStatusPending")}
                                </StatusPill>

                                {!isSigned && (
                                  <RowActions>
                                    <SecondaryButton
                                      size="small"
                                      iconName="duplicate"
                                      onClick={() =>
                                        logic.handleCopySigningLink(
                                          uniqueSigningUrl,
                                          participant.id,
                                        )
                                      }
                                    />
                                    <a
                                      href={uniqueSigningUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ textDecoration: "none" }}
                                    >
                                      <PrimaryButton
                                        size="small"
                                        label={t("General.Open")}
                                      />
                                    </a>
                                  </RowActions>
                                )}
                              </div>
                            </ParticipantRow>
                          );
                        })}
                      </ParticipantList>
                    </AccordionBody>
                  )}
                </AccordionItem>
              );
            })}
          </AgreementsContainer>
        )}
      </TransparentBox>

      <Modal
        label={
          logic.createdSigningUrl
            ? t("Staff.ProfilePanelAgreementCreatedHeader", "Success")
            : t("Staff.ProfilePanelCreateAgreementTitle")
        }
        isOpen={logic.isCreateModalOpen}
        size="medium"
        onClose={() => {
          logic.setIsCreateModalOpen(false);
          logic.resetForm();
        }}
        footer={
          logic.createdSigningUrl ? (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                width: "100%",
              }}
            >
              <SecondaryButton
                label={t("General.Cancel")}
                onClick={() => {
                  logic.setIsCreateModalOpen(false);
                  logic.resetForm();
                }}
              />
              <PrimaryButton
                label={t("Staff.ProfilePanelOpenAndSign", "Open & Sign")}
                onClick={() => {
                  if (logic.createdSigningUrl) {
                    window.open(logic.createdSigningUrl, "_blank");
                  }
                  logic.setIsCreateModalOpen(false);
                  logic.resetForm();
                }}
              />
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                width: "100%",
              }}
            >
              <SecondaryButton
                label={t("General.Cancel")}
                onClick={() => {
                  logic.setIsCreateModalOpen(false);
                  logic.resetForm();
                }}
                disabled={logic.isSaving}
              />
              <SecondaryButton
                label={t("Staff.ProfilePanelPreviewAgreement", "Preview")}
                onClick={logic.handlePreviewAgreement}
                disabled={logic.isSaving}
              />
              <PrimaryButton
                label={
                  logic.isSaving
                    ? t("General.Creating", "Creating...")
                    : t("General.Create", "Create")
                }
                onClick={logic.handleCreateAgreement}
                disabled={logic.isSaving}
              />
            </div>
          )
        }
      >
        {logic.createdSigningUrl ? (
          <CreateModalContent
            style={{ alignItems: "center", textAlign: "center", gap: "20px" }}
          >
            <CustomIcon name="checkmark" size="48px" color={ColorV2.Blue1} />
            <div
              style={{
                fontSize: "16px",
                fontWeight: 600,
                color: ColorV2.Blue1,
              }}
            >
              {t("Staff.ProfilePanelAgreementCreatedTitle")}
            </div>
            <div style={{ fontSize: "14px", color: ColorV2.Blue3 }}>
              {t("Staff.ProfilePanelAgreementCreatedDesc")}
            </div>
          </CreateModalContent>
        ) : (
          <CreateModalContent>
            <CreateModalScroll>
              <FormGrid>
                <InputField
                  fullWidth
                  label={t("Staff.ProfilePanelDocumentNameField")}
                  value={logic.newAgreementName}
                  onChange={logic.setNewAgreementName}
                  disabled={logic.isSaving}
                />

                <div>
                  <InputField
                    fullWidth
                    label={t("Staff.ProfilePanelEmployerSsnField")}
                    value={logic.employerSsn}
                    onChange={logic.setEmployerSsn}
                    disabled={logic.isSaving}
                    placeholder="YYYYMMDDXXXX"
                    hideClearButton={Boolean(logic.employerSsn)}
                  />
                  <FieldHelpText>
                    {t(
                      "Staff.ProfilePanelEmployerSsnHelpText",
                      "Används för BankID-signering",
                    )}
                  </FieldHelpText>
                </div>

                <InputField
                  fullWidth
                  label={t("Staff.ProfilePanelEmployerNameField")}
                  value={logic.employerName}
                  onChange={logic.setEmployerName}
                  disabled={logic.isSaving}
                  hideClearButton={Boolean(logic.employerName)}
                />

                <InputField
                  fullWidth
                  label={t(
                    "Staff.ProfilePanelRoleTitleField",
                    "Befattning / Roll",
                  )}
                  value={logic.roleTitle}
                  onChange={logic.setRoleTitle}
                  disabled={logic.isSaving}
                  hideClearButton={Boolean(logic.roleTitle)}
                />

                <Dropdown
                  fullWidth
                  label={t(
                    "Staff.ProfilePanelEmploymentTypeField",
                    "Anställningsform",
                  )}
                  value={logic.employmentType}
                  onChange={logic.handleEmploymentTypeChange}
                  options={CONTRACT_EMPLOYMENT_TYPE_OPTIONS}
                  disabled={logic.isSaving}
                />

                {logic.employmentType === "Provanställning" && (
                  <>
                    <ProbationContainer>
                      <label
                        style={{
                          fontSize: "14px",
                          color: ColorV2.Blue2,
                          fontWeight: 500,
                        }}
                      >
                        {t(
                          "Staff.ProfilePanelProbationPeriodField",
                          "Prövotid",
                        )}{" "}
                        *
                      </label>
                      <ProbationInputs>
                        <InputField
                          fullWidth
                          value={logic.probationDuration}
                          onChange={logic.handleProbationDurationChange}
                          placeholder="6"
                          disabled={logic.isSaving}
                        />
                        <Dropdown
                          fullWidth
                          value={logic.probationUnit}
                          onChange={logic.handleProbationUnitChange}
                          options={PROBATION_UNIT_OPTIONS}
                          disabled={logic.isSaving}
                        />
                      </ProbationInputs>
                    </ProbationContainer>

                    <InputField
                      fullWidth
                      label={t(
                        "Staff.ProfilePanelProbationEndDateField",
                        "Prövotid t.o.m.",
                      )}
                      value={logic.probationEndDate}
                      onChange={logic.setProbationEndDate}
                      disabled={logic.isSaving}
                      placeholder="YYYY-MM-DD"
                    />

                    <InputField
                      fullWidth
                      label={t(
                        "Staff.ProfilePanelProbationBecomesField",
                        "Övergår till",
                      )}
                      value="Tillsvidareanställning"
                      disabled
                    />
                  </>
                )}

                <InputField
                  fullWidth
                  label={t("Staff.ProfilePanelStartDateField", "Tillträdesdag")}
                  value={logic.startDate}
                  onChange={logic.handleStartDateChange}
                  disabled={logic.isSaving}
                />

                {logic.employmentType !== "Provanställning" &&
                  logic.employmentType !== "Tillsvidare" && (
                    <InputField
                      fullWidth
                      label={t(
                        "Staff.ProfilePanelEndDateField",
                        "Slutdatum (om tidsbegränsad)",
                      )}
                      value={logic.endDate}
                      onChange={logic.setEndDate}
                      disabled={logic.isSaving}
                    />
                  )}

                <InputField
                  fullWidth
                  label={t(
                    "Staff.ProfilePanelWorkPercentageField",
                    "Sysselsättningsgrad",
                  )}
                  value={logic.workPercentage}
                  onChange={logic.handleWorkPercentageChange}
                  disabled={logic.isSaving}
                />

                <InputField
                  fullWidth
                  label={t(
                    "Staff.ProfilePanelWorkingHoursPerWeekField",
                    "Arbetstidsmått",
                  )}
                  value={logic.weeklyHours}
                  onChange={logic.setWeeklyHours}
                  disabled={logic.isSaving}
                />

                <Dropdown
                  fullWidth
                  label={t(
                    "Staff.ProfilePanelWorkScheduleArrangementField",
                    "Arbetstidens förläggning",
                  )}
                  value={logic.workScheduleArrangement}
                  onChange={logic.setWorkScheduleArrangement}
                  options={WORK_SCHEDULE_ARRANGEMENT_OPTIONS}
                  disabled={logic.isSaving}
                />

                <InputField
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldWorkPlace", "Arbetsplats")}
                  value={logic.workPlace}
                  onChange={logic.setWorkPlace}
                  disabled={logic.isSaving}
                  hideClearButton={Boolean(logic.workPlace)}
                />

                <InputField
                  fullWidth
                  label={t("Staff.ProfilePanelSalaryTypeField", "Löneform")}
                  value={logic.salaryType}
                  onChange={logic.setSalaryType}
                  disabled={logic.isSaving}
                  hideClearButton={Boolean(logic.salaryType)}
                />

                <div>
                  <InputField
                    fullWidth
                    label={
                      logic.salaryType === "Hourly" ||
                      logic.salaryType === "Timlön"
                        ? t("Staff.ProfilePanelHourlySalary", "Timlön")
                        : logic.isPartTime
                          ? t(
                              "Staff.ProfilePanelFullTimeSalaryField",
                              "Heltidslön (100 %)",
                            )
                          : logic.salaryType === "Monthly" ||
                              logic.salaryType === "Månadslön"
                            ? t("Staff.ProfilePanelMonthlySalary", "Månadslön")
                            : t("Staff.ProfilePanelSalaryField", "Lön")
                    }
                    value={logic.salary}
                    onChange={logic.setSalary}
                    disabled={logic.isSaving}
                    hideClearButton={Boolean(logic.salary)}
                  />
                  {logic.isPartTime && logic.calculatedPartTimeSalary && (
                    <CalculatedSalaryNote>
                      {t("Staff.ProfilePanelCalculatedPartTimeSalaryNote", {
                        percentage: logic.workPercentage,
                        amount: logic.calculatedPartTimeSalary,
                      })}
                    </CalculatedSalaryNote>
                  )}
                </div>

                <InputField
                  fullWidth
                  label={t(
                    "Staff.ProfilePanelVacationDaysField",
                    "Semesterdagar / år",
                  )}
                  value={logic.vacationDays}
                  onChange={logic.setVacationDays}
                  disabled={logic.isSaving}
                />

                <InputField
                  fullWidth
                  label={t(
                    "Staff.ProfilePanelNoticeDaysField",
                    "Uppsägningstid",
                  )}
                  value={logic.noticeDays}
                  onChange={logic.setNoticeDays}
                  disabled={logic.isSaving}
                  placeholder="30 dagar"
                />

                <InputField
                  fullWidth
                  label={t(
                    "Staff.ProfilePanelCollectiveAgreementField",
                    "Kollektivavtal",
                  )}
                  value={logic.collectiveAgreement}
                  onChange={logic.setCollectiveAgreement}
                  disabled={logic.isSaving}
                  placeholder={t(
                    "Staff.ProfilePanelCollectiveAgreementPlaceholder",
                    "T.ex. Hotell- och restaurangavtalet (lämna tomt om ej tillämpligt)",
                  )}
                />

                {/* <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}
                >
                  <label style={{ fontSize: "14px", color: ColorV2.Blue2 }}>
                    {t("Staff.ProfilePanelCustomFileLabel")}
                  </label>
                  <input
                    type="file"
                    accept=".pdf"
                    disabled={logic.isSaving}
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      logic.setCustomFile(file);
                    }}
                    style={{
                      fontSize: "14px",
                      color: ColorV2.Blue1,
                      padding: "8px 12px",
                      background: ColorV2.White,
                      border: `1px solid ${ColorV2.Blue6}`,
                      borderRadius: "8px",
                    }}
                  />
                </div> */}
              </FormGrid>

              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <SpecialTermsHeader>
                  <SpecialTermsLabel>
                    {t(
                      "Staff.ProfilePanelSpecialTermsField",
                      "Särskilda villkor",
                    )}
                  </SpecialTermsLabel>
                  <SpecialTermsHelpText>
                    {t(
                      "Staff.ProfilePanelSpecialTermsHelpText",
                      "Ange eventuella villkor som gäller utöver uppgifterna ovan, till exempel arbetstid, övertidsersättning, tjänstepension, försäkringar eller andra överenskommelser.",
                    )}
                  </SpecialTermsHelpText>
                </SpecialTermsHeader>
                <StyledTextArea
                  value={logic.otherTerms}
                  onChange={(e) => logic.setOtherTerms(e.target.value)}
                  disabled={logic.isSaving}
                  placeholder={t(
                    "Staff.ProfilePanelSpecialTermsPlaceholder",
                    "T.ex. övertidsersättning, tjänstepension eller särskilda arbetstider",
                  )}
                />
                <SpecialTermsInfo>
                  {t(
                    "Staff.ProfilePanelSpecialTermsInfo",
                    "Innehållet visas under Särskilda villkor i anställningsavtalet. Fältet kan lämnas tomt.",
                  )}
                </SpecialTermsInfo>
              </div>
            </CreateModalScroll>

            {logic.saveError && (
              <div style={{ color: ColorV2.NegativeRed, fontSize: "14px" }}>
                {t(logic.saveError, logic.saveError)}
              </div>
            )}
          </CreateModalContent>
        )}
      </Modal>

      <Modal
        label={t(
          "Staff.ProfilePanelUploadAgreementTitle",
          "Ladda upp befintligt avtal",
        )}
        isOpen={logic.isUploadModalOpen}
        size="medium"
        onClose={() => {
          logic.setIsUploadModalOpen(false);
          logic.resetUploadForm();
        }}
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              width: "100%",
            }}
          >
            <SecondaryButton
              label={t("General.Cancel")}
              onClick={() => {
                logic.setIsUploadModalOpen(false);
                logic.resetUploadForm();
              }}
              disabled={logic.isUploading}
            />
            <PrimaryButton
              label={
                logic.isUploading
                  ? t("Staff.ProfilePanelUploading", "Laddar upp...")
                  : t("Staff.ProfilePanelUpload", "Ladda upp")
              }
              onClick={logic.handleUploadAgreement}
              disabled={logic.isUploading || !logic.uploadFile}
            />
          </div>
        }
      >
        <CreateModalContent>
          <div
            style={{
              fontSize: "14px",
              color: ColorV2.Blue3,
              marginBottom: "8px",
            }}
          >
            {t(
              "Staff.ProfilePanelUploadAgreementDesc",
              "Ladda upp ett befintligt dokument eller avtal. Dokumentet markeras som klart direkt.",
            )}
          </div>

          <CreateModalScroll>
            <FormGrid>
              <div
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                <label
                  style={{
                    fontSize: "14px",
                    color: ColorV2.Blue2,
                    fontWeight: 500,
                  }}
                >
                  PDF-dokument (max 25MB) *
                </label>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  disabled={logic.isUploading}
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    logic.setUploadFile(file);
                    if (file && !logic.uploadDocumentName) {
                      logic.setUploadDocumentName(
                        file.name.replace(/\.pdf$/i, ""),
                      );
                    }
                  }}
                  style={{
                    fontSize: "14px",
                    color: ColorV2.Blue1,
                    padding: "10px 12px",
                    background: ColorV2.White,
                    border: `1px solid ${ColorV2.Blue6}`,
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                />
              </div>

              <InputField
                fullWidth
                label={t("Staff.ProfilePanelDocumentNameField", "Dokumentnamn")}
                value={logic.uploadDocumentName}
                onChange={logic.setUploadDocumentName}
                disabled={logic.isUploading}
                placeholder="T.ex. Anställningsavtal 2024"
              />

              <Dropdown
                fullWidth
                label={t("Staff.ProfilePanelCategory", "Kategori")}
                value={logic.uploadCategory}
                onChange={logic.setUploadCategory}
                disabled={logic.isUploading}
                options={[
                  {
                    value: "employment",
                    label: t(
                      "Staff.ProfilePanelCategoryEmployment",
                      "Anställningsavtal",
                    ),
                  },
                  {
                    value: "other",
                    label: t("Staff.ProfilePanelCategoryOther", "Övrigt"),
                  },
                ]}
              />
            </FormGrid>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              <label style={{ fontSize: "14px", color: ColorV2.Blue2 }}>
                {t(
                  "Staff.ProfilePanelNotes",
                  "Anteckningar / Referens (valfritt)",
                )}
              </label>
              <StyledTextArea
                value={logic.uploadNotes}
                onChange={(e) => logic.setUploadNotes(e.target.value)}
                disabled={logic.isUploading}
                placeholder={t(
                  "Staff.ProfilePanelNotes",
                  "Anteckningar / Referens (t.ex. ärendenummer i Scrive)",
                )}
              />
            </div>
          </CreateModalScroll>

          {logic.uploadError && (
            <div style={{ color: ColorV2.NegativeRed, fontSize: "14px" }}>
              {t(logic.uploadError, logic.uploadError)}
            </div>
          )}
        </CreateModalContent>
      </Modal>
    </Section>
  );
};
